import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useTheme from '@guestyci/foundation/useTheme';
import TextField from '@guestyci/foundation/TextField';
import IconButton from '@guestyci/foundation/IconButton';
import defaultTheme from '@guestyci/foundation/theme/defaultTheme';
import createStyles from '@guestyci/foundation/createStyles';
import { Section, Row } from '@guestyci/foundation/Layout';
import { ReactComponent as CloseIcon } from '@guestyci/icons/BtnDelete.svg';

import ls from 'utils/localStorageHandler';
import utils from 'utils';
import getUserData from 'utils/user';
import useKycStatus from 'app/hooks/user/useKycStatus';
import { RootState } from 'app/store';

const useStyles = createStyles((theme: typeof defaultTheme) => ({
  root: {
    left: 0,
    width: '100%',
    zIndex: 100,
    height: 'auto',
    bottom: 0,
    padding: theme.spacer(2),
    position: 'absolute',
    textAlign: 'center',
    alignItems: 'start',

    [theme.breakpoints.create('md')]: {
      height: '100%',
      maxHeight: 100,
      padding: theme.spacer(2, 6),
      position: 'static',
      alignItems: 'center',
    },
  },
  content: {
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: theme.spacer(1),
    textAlign: 'left',

    [theme.breakpoints.create('sm')]: {
      gap: theme.spacer(4),
      alignItems: 'center',
      flexWrap: 'nowrap',
    },
  },
  ctaLink: {
    display: 'flex',
    justifyContent: 'end',
    width: '100%',
    marginBottom: theme.spacer(2),
    fontWeight: 600,
    textDecoration: 'underline',

    [theme.breakpoints.create('sm')]: {
      display: 'inline',
      width: 'auto',
      marginBottom: 0,
    },
  },
  closeBtn: {
    flexShrink: 0,
  },
}));

const useDelayTillNextYear = (statusCompleted: boolean | undefined) => {
  useEffect(() => {
    if (!statusCompleted) return;
    ls.setLocalStorage({ kycDelayedTo: utils.getNextYear() });
  }, [statusCompleted]);
};

const KycBanner = () => {
  const { kycDelayedTo } = ls.getLocalStorageByKey('kycDelayedTo');
  const shouldRun = !kycDelayedTo || utils.isTodayOrPastDate(kycDelayedTo);

  const [isBannerOpen, setIsBannerOpen] = useState(shouldRun);
  const theme = useTheme() as unknown as typeof defaultTheme;
  const classes = useStyles();
  const { data, isFetched } = useKycStatus(shouldRun);
  const { subscribed } = getUserData() || {};
  const trialEndDate = useSelector<RootState, string>(
    (state) => state.trialPeriod.trialEndDate
  );
  const daysSinceTrialEnd = utils.getDaysDifference(trialEndDate, utils.getToday());

  const isOpen =
    daysSinceTrialEnd < -30 &&
    subscribed &&
    isFetched &&
    data?.isPending &&
    isBannerOpen;

  useDelayTillNextYear(data?.isCompleted);

  const delayNotification = useCallback(() => {
    ls.setLocalStorage({ kycDelayedTo: utils.getTomorrow() });
    setIsBannerOpen(false);
  }, []);

  if (!isOpen) return null;

  return (
    <Section
      className={classes.root}
      bgColor={theme.palette.error.background}
      justify="between"
      wrap={false}
    >
      <div />

      <Row className={classes.content}>
        <TextField bold>
          Authentication needed
        </TextField>

        <TextField>
          Authenticate your user to continue using Guesty in a simple
          procedure.
        </TextField>

        <a
          href={data?.url}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.ctaLink}
          onClick={delayNotification}
        >
          <TextField>Click here to start</TextField>
        </a>
      </Row>

      <IconButton onClick={delayNotification} className={classes.closeBtn}>
        <CloseIcon width={12} height={12} />
      </IconButton>
    </Section>
  );
};

export default KycBanner;
